import { useState } from "react";
import { useIntl } from "react-intl";
import { useBudgetEdit, IUseBudgetEditOverrides } from "./hooks/useBudgetEdit";
import { useBudgetCanAct } from "./hooks/useBudgetCanAct";

import { BudgetVehicleCard } from "../components/BudgetVehicleCard";
import { ServiceSummary } from "./components/ServiceSummary";
import { ManagerService } from "./components/ManagerService";
import { ServiceSkeleton } from "../components/ServiceSkeleton";
import { CounterProposal } from "./components/CounterProposal";
import { BudgetEditActions } from "./components/BudgetEditActions";
import { BudgetHeader } from "../components/BudgetHeader";
import { BudgetSummary } from "../components/BudgetSummary";
import { BudgetAttachments } from "../components/budgetAttachments";

import { IBudget } from "../../../models/budgetV2";

import "../index.css";

const SkeletonBlock = ({ className = "" }: { className?: string }) => (
    <div className={`animate-pulse rounded bg-gray-300 ${className}`} />
);

interface IBudgetManagerEditProps extends IUseBudgetEditOverrides {
    persona: string;
    onClose?: () => void;
    onBreadcrumbClick?: () => void;
}

const BudgetManagerEdit = (props: IBudgetManagerEditProps) => {
    const intl = useIntl();

    const { persona, onClose, onBreadcrumbClick, ...overrides } = props;

    const {
        budget, truckerPayPlan, loading, statusLabel, statusBadgeClass, approvedDateLabel, hasBudgetChanges, submitting,
        setBudget, showCoverageBalanceBox, saveBudgetUpdate, acceptBudget, rejectBudget, counterProposeBudget,
        attachments,
    } = useBudgetEdit(persona, overrides);

    const [counterProposalValue, setCounterProposalValue] = useState<number>(0);
    const [counterProposalObservation, setCounterProposalObservation] = useState<string>("");

    const canAct = useBudgetCanAct(budget, loading, persona);

    return (
        <div className="w-full">
            <BudgetHeader persona={persona} budget={budget} loading={loading} onClose={onClose} onBreadcrumbClick={onBreadcrumbClick} />

            {<BudgetVehicleCard budget={budget} loading={loading || !budget} />}

            {showCoverageBalanceBox() ? (
                <div className='mb-6 px-6'>
                    <div className={`
                            flex flex-row items-center justify-between padding-y-5 padding-x-15 margin-bottom-10
                            ${(truckerPayPlan?.availableCoverageBalance ?? 0) > 0 ? 'budget-coverage-balance' : 'budget-coverage-insufficient-balance'}`}
                    >
                        <div>
                            <div className="text-medium mr-10 text-size-18">{intl.formatMessage({ id: 'intl-msg:budgets.availableBalance' })}</div>
                            <p>{intl.formatMessage({ id: 'intl-msg:budgets.availableBalanceDescription' })}</p>
                        </div>
                        <div className='flex flex-col items-end shrink-0 pt-2'>
                            <span className={`font-bold ${(truckerPayPlan?.availableCoverageBalance ?? 0) > 0 ? '' : 'budget-coverage-insufficient-balance-value'} text-size-20`}>
                                {(truckerPayPlan?.availableCoverageBalance ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                        </div>
                    </div>
                </div>
            ) : null}

            {loading ? <ServiceSkeleton /> : truckerPayPlan ? (
                <div className={`mb-6 px-6`}>
                    <div className={`font-semibold mb-4 text-size-18`}>{intl.formatMessage({ id: 'intl-msg:budgets.services' })}</div>
                    {(budget?.versionData?.services ?? []).map((service, index) => (
                        <ServiceSummary
                            key={service.id}
                            service={service}
                            serviceIndex={index}
                            truckerPay={truckerPayPlan}
                        />
                    ))}
                </div>
            ) : (
                <div className={`mb-6 px-6`}>
                    <div className={`font-semibold mb-4 text-size-18`}>{intl.formatMessage({ id: 'intl-msg:budgets.services' })}</div>
                    {(budget?.versionData?.services ?? []).map((service, index) => (
                        <ManagerService
                            key={service.id}
                            service={service}
                            serviceIndex={index}
                        />
                    ))}
                </div>
            )}

            {!loading ? <BudgetSummary persona={persona} budget={budget} truckerPayPlan={truckerPayPlan} /> : <SkeletonBlock className="h-[100px] w-full mb-4" />}

            {!loading && (
                <BudgetAttachments
                    files={attachments}
                    budgetId={budget?.uuid}
                    allowUpload={false}
                />
            )}

            <CounterProposal
                budget={budget}
                readOnly={!canAct}
                onValueChange={setCounterProposalValue}
                onObservationChange={setCounterProposalObservation}
            />

            <div className={`mb-6 px-6`}>
                <div className={`font-bold mb-4 flex flex-row items-center gap-2 justify-between`}>
                    <span
                        className={`min-w-60 px-3 py-2 rounded-full text-size-12 text-center inline-block whitespace-nowrap ${statusBadgeClass}`}
                    >
                        {statusLabel}
                    </span>
                    <span>{approvedDateLabel}</span>
                </div>
            </div>

            {canAct && (
                <BudgetEditActions
                    hasChanges={hasBudgetChanges}
                    submitting={submitting}
                    onSaveUpdate={saveBudgetUpdate}
                    onAccept={acceptBudget}
                    onReject={rejectBudget}
                    canCounterPropose={counterProposalValue > 0}
                    onCounterPropose={() => counterProposeBudget(counterProposalValue, counterProposalObservation)}
                />
            )}
        </div>
    );
};

export default BudgetManagerEdit;